/**
 * AI 资料员 — assistant text "专业感" sanitizer (前端 streaming hook).
 *
 * **职责边界**: 这是 **user-facing 文案 sanitize hook**, 只拦 assistant text 流
 * 给用户的部分. **绝不**碰 LLM 的 tool_use args (工具调用 args 校验是另一套
 * "工具执行 hook", 在 Claw 后端的 user-ask-tool / bash-guard 等). 两套独立.
 *
 * 跟 useSSEChat 里的 classifyErrorMessage 同款思路 — 后端 LLM 输出做不到 100%
 * 遵守 prompt 反例, 前端在 SSE text-delta 累积时跑一遍 idempotent 替换, 把
 * 不该出现在用户面前的字眼**剪掉**.
 *
 * **覆盖 4 类规则** (全部从 agents.ts 反例段聚合上来):
 *   1. HEDGE_PATTERNS — 弱化词: 我猜 / 应该是 / 可能是 / 我觉得 / 似乎 / ...
 *      (源: agents.ts 沟通风格段)
 *   2. MARKER_PATTERNS — hidden XML tag: <business-context> / <aiclerk-task-init> / ...
 *      (源: agents.ts 沟通风格段)
 *   3. STAGE_PATTERNS — 内部 SOP 阶段名: Step N / 第 N 步 / 5 步骨架 / fallback 路径
 *      (源: agents.ts 沟通风格段)
 *   4. INTERNAL_COORD_PATTERNS — 内部坐标 / id / 路径: r0,c0 / snapshot_seq /
 *      0-based / uploads/ / photo_search_xxx.json
 *      (源: agents.ts 填表执行细则末段)
 *
 * 设计原则:
 *   - **Idempotent** — 同一段文本反复跑同样结果, 配合 text-delta chunk 累加
 *     可以每个 chunk 都重跑 (O(n²) 但 text 一般 <5KB, regex 替换毫秒级)
 *   - **保守替换不伤句意** — 整词整段删, 不做语义重写; 弱化"应该是" → "是",
 *     不动单字"应"出现在其他词里 (e.g. "应用", "答应")
 *   - **替换不影响 markdown 结构** — 不动 \`\`\` / | / # 等结构字符
 *
 * 添加新规则的 SOP:
 *   1. agents.ts 里**先删那段反例** (减 prompt 负担)
 *   2. 在下面对应 *_PATTERNS 数组里加一行 regex + 替换
 *   3. 重启 dev server (HMR 立刻生效, 不需要 build 后端)
 *
 * iOS 类比: 类似 UITextField 的 shouldChangeCharactersIn delegate 过滤器 —
 * 用户输入流过来时实时过滤掉不符合规则的字符. 这里把"用户"换成"LLM stream".
 */

/**
 * 弱化词 / hedging — 用户读着觉得 AI 不专业.
 * 注意中文整词替换避免破坏多字词 (e.g. "应"字单删会破坏"应用"/"答应").
 */
const HEDGE_PATTERNS: Array<[RegExp, string]> = [
  // "我猜" / "猜下" 整词删
  [/我猜[:：]?\s*/g, ''],
  [/猜下[:：]?\s*/g, ''],
  [/猜测[:：]?\s*/g, ''],
  // "我觉得" / "我以为" 整词删
  [/我觉得\s*/g, ''],
  [/我以为\s*/g, ''],
  // 强弱化复合词 — "应该是" / "可能是" / "大概是" → "是" (保留断言)
  [/\s*应该\s*是\s*/g, '是'],
  [/\s*可能\s*是\s*/g, '是'],
  [/\s*大概\s*是\s*/g, '是'],
  [/\s*或许\s*是\s*/g, '是'],
  [/\s*也许\s*是\s*/g, '是'],
  // "似乎" / "好像" / "貌似" + 是/像 → 笃定
  [/\s*似乎\s*(是|像)\s*/g, '$1'],
  [/\s*好像\s*(是|像)\s*/g, '$1'],
  [/\s*貌似\s*(是|像)\s*/g, '$1'],
  // 句首 "估计" / "兴许" — 整词删
  [/(^|\n|。|，|,|;|；)\s*估计\s*/g, '$1'],
  [/(^|\n|。|，|,|;|；)\s*兴许\s*/g, '$1'],
  // hedge 句
  [/如果我没理解错[，,。]?\s*/g, ''],
  [/不知道对不对[，,。]?\s*/g, ''],
  [/也不太确定[，,。]?\s*/g, '']
]

/**
 * 内部 hidden marker — prompt 给 LLM 看的 XML tag, 用户根本不知道存在.
 * 整段删 (含 close tag 内容).
 */
const MARKER_PATTERNS: Array<[RegExp, string]> = [
  [/<business-context>[\s\S]*?<\/business-context>/g, ''],
  [/<aiclerk-task-init>[\s\S]*?<\/aiclerk-task-init>/g, ''],
  [/<current-doc>[\s\S]*?<\/current-doc>/g, ''],
  [/<aiclerk-scene>[\s\S]*?<\/aiclerk-scene>/g, ''],
  [/<aiclerk-task-name>[\s\S]*?<\/aiclerk-task-name>/g, ''],
  [/<aiclerk-blank-guide>[\s\S]*?<\/aiclerk-blank-guide>/g, ''],
  [/<aiclerk-sample-context>[\s\S]*?<\/aiclerk-sample-context>/g, ''],
  [/<message-attachments>[\s\S]*?<\/message-attachments>/g, ''],
  // 单独 marker 名 (LLM 偶尔会"提到" 不带尖括号, e.g. "business-context 标签")
  // 不能整词删 (业务字面也可能用 "business" "context" 字), 这里只拦带"标签"修饰的
  [/\s*[`'"]?<?business-context>?[`'"]?\s*(标签|标记|tag)\s*/g, ' 业务背景 '],
  [/这份文档没有附\s*[`'"]?<?business-context>?[`'"]?\s*[，,。]?\s*/g, '']
]

/**
 * 内部坐标 / id / 路径 — 工具协议 / NAS 路径术语, 用户视角看不到也不懂.
 * **不动 LLM 的 reasoning + tool args**, 只拦 user-facing assistant text.
 *
 * 设计: 大多数项替换成空, 上下文留给 LLM 自然包补 — 替换后句子结构仍读通.
 *   "空位在r0/r2/r4" → "空位在" (后面 LLM 自然有补充, 或读不通用户会问)
 * 关键: 这些 token 同时也是**正确 tool args 用法**, 不能改 LLM 输出给工具的 JSON.
 * 由于 sanitize 跑在 SSE text-delta 流 (assistant 给用户的文本), 不会碰 tool_use args.
 */
const INTERNAL_COORD_PATTERNS: Array<[RegExp, string]> = [
  // 坐标记法 — r0/c0, r2,c1, row=N, col=N 等
  [/\br\d+\s*[,，/、]\s*c\d+\b/g, ''], // r0,c0 / r2,c1
  [/\br\d+(?:\s*[/,，、]\s*r\d+)+/g, ''], // r0/r2/r4
  [/\b[rR]ow\s*=\s*\d+\b/g, ''],
  [/\b[cC]ol\s*=\s*\d+\b/g, ''],
  // 单独 r0/c0 在表达 "(rN)" / "rN:" / "rN(" 这种 — 替换成空, 上下文修剪
  [/\(\s*r\d+\s*\)/g, ''],
  [/[rR]\d+(?=[:：(（\s])/g, ''],
  // 0-based / 1-based / 下标 / 索引
  [/\s*0-based\s*/g, ''],
  [/\s*1-based\s*/g, ''],
  [/\s*下标\s*/g, ''],
  [/\s*索引\s*/g, ''],
  // snapshot_seq / table_seq / table_id / snapshot — 工具 id
  [/\s*snapshot_seq\s*[=:：]?\s*\w*/g, ''],
  [/\s*table_seq\s*[=:：]?\s*\w*/g, ''],
  [/\s*table_id\s*[=:：]?\s*\w*/g, ''],
  [/(?<![a-z])snapshot(?![a-zA-Z_-])\s*/g, ''], // 单独 snapshot, 不动 snapshot_xxx 复合
  // 路径 — uploads/<filename> / 内部缓存路径 photo_search_xxx.json
  [/\buploads\/[^\s,，。)]+/g, ''],
  [/\bphoto_search_[\w\-.]+\.json\b/g, ''],
  [/\$XHEY_CACHE_DIR\b/g, ''],
  [/\brel_path\b\s*[=:：]?\s*['"`]?[^\s'"`)，,]*['"`]?/g, '']
]

/**
 * 内部阶段术语 — Step N / 第 N 步 / 5 步骨架 等内部 SOP 命名, 用户不需要知道.
 * 替换为空 + 上下文修剪.
 */
const STAGE_PATTERNS: Array<[RegExp, string]> = [
  // 完整短语 — "进入 Step 2 之前" / "在 Step 4 排数据" / "继续 Step 3" 等
  [
    /(在|进入|到了|继续|当前|现在)\s*Step\s*\d+\s*(排数据|摆方案|动手|阶段|之前|之后)?\s*[，,：:]?\s*/g,
    ''
  ],
  [/Step\s*\d+\s*(排数据|摆方案|动手|阶段)\s*/g, ''],
  // 孤立 "Step 2" — 整段删
  [/\s*Step\s*\d+\s*/g, ''],
  // 中文 "第 N 步"
  [/\s*第\s*\d+\s*步\s*/g, ''],
  // meta 词
  [/\s*5\s*步骨架\s*/g, ''],
  [/\s*fallback\s*路径\s*/g, '']
]

/**
 * 应用所有规则. Idempotent — 跑 N 次结果相同.
 */
export function sanitizeAssistantText(text: string): string {
  if (!text) return text
  let out = text
  for (const [pattern, replace] of HEDGE_PATTERNS) {
    out = out.replace(pattern, replace)
  }
  for (const [pattern, replace] of MARKER_PATTERNS) {
    out = out.replace(pattern, replace)
  }
  for (const [pattern, replace] of STAGE_PATTERNS) {
    out = out.replace(pattern, replace)
  }
  for (const [pattern, replace] of INTERNAL_COORD_PATTERNS) {
    out = out.replace(pattern, replace)
  }
  // 收尾: 连续多空格 / 多换行收紧 (替换可能留下双空格 / "， 。" 这种)
  out = out
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/，\s*。/g, '。')
    .replace(/，\s*，/g, '，')
    .replace(/\n{3,}/g, '\n\n')
  return out
}
